import { rotationRadiansY } from "../../domain/viewer/saunaRotation";

/** Radians of slop allowed when deciding the transform has landed. */
export const SETTLED_EPSILON_RAD = 1e-6;

/**
 * "Has the requested rotation actually reached the group yet?"
 *
 * Extracted from `SaunaRig` because it is the whole of the subtle part, and
 * because a component that renders an R3F intrinsic cannot be unit-tested in
 * this repo (the DOM-level tests mock the reconciler, and React then warns
 * about the unknown `<group>` tag, which `src/test/setup.ts` fails on).
 *
 * ## Why this needs to exist at all
 *
 * `<Canvas>` renders its children into a SEPARATE R3F root — `await
 * root.configure(...)` then `root.render(children)` — so the commit that
 * writes `rotation-y` onto the group lands at least a microtask and a
 * scheduler lane after the outer tree's effects have run. Measuring the group
 * from an outer effect therefore reads the OLD angle and produces a
 * bit-identical bounding box, which the consumer's equality check then
 * discards: the shadow frustum silently keeps the previous orientation.
 * `useLayoutEffect` does not help — it is the wrong root, not the wrong phase.
 *
 * So the rule is observational rather than scheduled: an effect only says
 * "this angle is pending", and a frame callback asks this tracker whether the
 * transform is visible on the object yet.
 *
 * Two properties matter as much as the timing:
 *
 * - **One shot per request.** Consumers re-bake a one-shot contact shadow and
 *   re-fit a shadow camera; firing every frame would be ruinous.
 * - **Nothing pending unless the REQUEST changed.** A live drag writes
 *   `rotation.y` imperatively without touching the prop, so it must not
 *   trigger anything until it commits.
 */
export interface RotationSettleTracker {
	/**
	 * Note the angle the UI is now asking for. Returns true when this is a new
	 * request that will need settling (i.e. the caller should invalidate the
	 * demand frameloop).
	 */
	request(rotationDeg: number): boolean;
	/**
	 * Called once per rendered frame with the object's live `rotation.y`.
	 * Returns true exactly once per request, on the first frame where the
	 * transform has arrived.
	 */
	settle(currentRotationY: number): boolean;
	/** Test/diagnostic view of the state machine. */
	readonly pending: boolean;
	readonly settledRotationDeg: number;
}

export function createRotationSettleTracker(
	initialRotationDeg: number,
): RotationSettleTracker {
	// Seeded with the mount angle: the FIRST measurement belongs to whoever
	// builds the geometry, which happens with real meshes in the scene rather
	// than an empty rig.
	let settledRotationDeg = initialRotationDeg;
	let requestedRotationDeg = initialRotationDeg;
	let pending = false;

	return {
		request(rotationDeg: number) {
			requestedRotationDeg = rotationDeg;
			// Exact degree equality here, while `settle` compares NORMALIZED
			// radians. The two can only disagree for angles that normalize onto
			// each other (-90 vs 270, 360 vs 0), which the committed
			// `SaunaRotationDeg` (0|90|180|270) cannot produce — normalizing
			// here would be dead code, so it is left explicit instead.
			if (settledRotationDeg === rotationDeg) {
				// Back where we started (an undo, or a drag that returned home)
				// — there is nothing to re-measure.
				pending = false;
				return false;
			}
			pending = true;
			return true;
		},
		settle(currentRotationY: number) {
			if (!pending) return false;
			const target = rotationRadiansY(requestedRotationDeg);
			if (Math.abs(currentRotationY - target) > SETTLED_EPSILON_RAD) {
				return false;
			}
			pending = false;
			settledRotationDeg = requestedRotationDeg;
			return true;
		},
		get pending() {
			return pending;
		},
		get settledRotationDeg() {
			return settledRotationDeg;
		},
	};
}
